import React from 'react';
import { useState } from 'react';
import { ArrowPathIcon, CreditCardIcon } from '@heroicons/react/24/outline';
import { Button } from 'components/ui';
import { hasPermission } from 'utils/permissionsUtils';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { XMarkIcon } from '@heroicons/react/24/solid';
import clsx from 'clsx';
import { CheckIcon } from '@heroicons/react/24/solid';

export const PaymentHeader = ({
  transactionCount,
  syncing,
  loading,
  isMobile,
  paidLinkExists,
  canCreatePaymentLink = true,
  paymentLinkBlockedReason = null,
  gateways,              // NEW
  selectedGateway,       // NEW
  onSelectGateway,       // NEW
  onRefresh,
  onCreatePaymentLink,
  isStudent = false,
}) => {
  const [openDropdown, setOpenDropdown] = useState(false);
  // --- Payment Gateway Dropdown (NEW IMPROVED) ---
  const renderGatewayDropdown = () => {
    const isOpen = openDropdown === "gateway";
    const selectedGatewayName =
      selectedGateway?.GatewayName || "All Payment Gateway";

    return (
      <div className="relative mt-1 w-full sm:w-56">
        {/* Trigger Button */}
        <Button
          variant="outlined"
          type="button"
          className={clsx(
            "flex w-full items-center justify-between px-3 py-2 text-sm border transition-colors",
            selectedGateway
              ? "border-blue-300 bg-blue-50 text-blue-700 dark:border-blue-700 dark:bg-blue-900/20 dark:text-blue-300"
              : "border-gray-300 bg-white text-gray-700 dark:bg-gray-800 dark:border-dark-600 dark:text-dark-200",
            "hover:border-blue-400 dark:hover:border-blue-500"
          )}
          onClick={() => setOpenDropdown(isOpen ? null : "gateway")}
        >
          <span className="truncate">{selectedGatewayName}</span>

          <div className="flex items-center gap-1">
            {/* Clear Gateway Filter */}
            {selectedGateway && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onSelectGateway(null); // clear selection
                }}
                className="rounded p-0.5 hover:bg-blue-200 dark:hover:bg-blue-800 transition-colors"
              >
                <XMarkIcon className="size-3.5" />
              </button>
            )}
            <ChevronDownIcon
              className={clsx(
                "size-4 transition-transform",
                selectedGateway
                  ? "text-blue-600 dark:text-blue-400"
                  : "text-gray-500 dark:text-dark-400",
                isOpen && "rotate-180"
              )}
            />
          </div>
        </Button>

        {/* Dropdown List */}
        {isOpen && (
          <div className="absolute z-50 mt-1 w-full max-h-48 overflow-y-auto rounded-lg 
          border border-gray-200 bg-white dark:bg-gray-800 dark:border-dark-600 shadow-lg">

            {/* Select All Option */}
            <button
              onClick={() => onSelectGateway(null)}
              className={clsx(
                "w-full px-3 py-2 flex items-center gap-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors",
                !selectedGateway && "bg-primary-50 dark:bg-primary-900/20"
              )}
            >
              {!selectedGateway && (
                <CheckIcon className="size-4 text-primary-600 dark:text-primary-400" />
              )}
              <span>All Gateways</span>
            </button>

            {/* Gateway Options */}
            {gateways?.length > 0 ? (
              gateways.map((gateway) => (
                <button
                  key={gateway.GatewayId}
                  onClick={() => {
                    onSelectGateway(gateway);
                    setOpenDropdown(null);
                  }}
                  className={clsx(
                    "w-full px-3 py-2 flex items-center gap-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors",
                    selectedGateway?.GatewayId === gateway.GatewayId &&
                    "bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-300"
                  )}
                >
                  {selectedGateway?.GatewayId === gateway.GatewayId && (
                    <CheckIcon className="size-4 text-primary-600 dark:text-primary-400" />
                  )}
                  <span>{gateway.GatewayName}</span>
                </button>
              ))
            ) : (
              <p className="px-4 py-2 text-gray-500 text-sm">No gateways found</p>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Header Section */}
      <div className="flex flex-col gap-2">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 sm:text-xl">
          Payment Links
        </h2>
        <p className="text-xs text-gray-600 dark:text-gray-400 sm:text-sm">
          Track and manage payment links generated for this lead
        </p>
      </div>

      {/* Actions Section */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        {/* Transaction Count Badge */}
        <div className="flex items-center">
          <div className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-700 dark:bg-gray-700 dark:text-gray-300">
            <span className="font-semibold">{transactionCount}</span>
            <span className="ml-1 text-gray-500 dark:text-gray-400">
              {transactionCount === 1 ? 'link' : 'links'}
            </span>
          </div>
          <div className="ml-3">{renderGatewayDropdown()}</div>
        </div>



        {/* Action Buttons */}
        <div className="flex items-center gap-2 sm:gap-3">
          {/* Refresh Button - Hide when payment is successful */}
          {!paidLinkExists && (
            <Button
              variant="outlined"
              size="sm"
              onClick={onRefresh}
              disabled={syncing || loading || isStudent}
              className={`
                ${isMobile
                  ? 'px-3 py-2'
                  : 'px-4 py-2'
                }
                transition-all duration-200 hover:shadow-sm
                ${isStudent ? 'opacity-60 cursor-not-allowed' : ''}
              `}
            >
              <ArrowPathIcon
                className={`
                  h-4 w-4 
                  ${syncing ? 'animate-spin' : ''}
                  ${isMobile ? '' : 'mr-2'}
                `}
              />
              {isMobile ? (
                <span className="sr-only">Refresh</span>
              ) : (
                <span>{syncing ? 'Refreshing...' : 'Refresh'}</span>
              )}
            </Button>
          )}

          {/* Create Payment Link Button */}
          {hasPermission("MLLE") && !paidLinkExists && (
            <Button
              variant="soft"
              color={canCreatePaymentLink && !isStudent ? "primary" : "neutral"}
              size="sm"
              onClick={onCreatePaymentLink}
              disabled={!canCreatePaymentLink || isStudent}
              title={isStudent ? 'Cannot create payment link for student' : !canCreatePaymentLink ? paymentLinkBlockedReason : 'Create a new payment link'}
              className={`
                transition-all duration-200 
                ${canCreatePaymentLink && !isStudent ? 'hover:shadow-sm hover:scale-[1.02]' : 'cursor-not-allowed opacity-60'}
                ${isMobile
                  ? 'px-4 py-2'
                  : 'px-6 py-2'
                }
              `}
            >
              <CreditCardIcon className="h-4 w-4 mr-2" />
              {isMobile ? 'Create' : 'Create Payment Link'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
